import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Head from '../../../layout/head/Head';
import Content from '../../../layout/content/Content';
import TablePageHeading from '../components/page_heading/TablePageHeading';
import * as app_consts from "../../../constants/react_app_consts";
import { useSelector } from 'react-redux';
import TableList from '../components/table/TableList';
import { findModuleAction, formatNumber, formatToSentenceCase } from '../../../utils/functionalUtils';
import { employee_dashboard_actions_helper } from '../../../actions/actions_helper';
import { fail_retrieve_loading_order_list, loading_retrieve_loading_order_list, reset_retrieve_loading_order_list, success_retrieve_loading_order_list } from '../../../reducers/dashboard_reducers/loading_orders/retrieve_loading_order_list_Slice';
import { RETRIEVE_LOADING_ORDER_LIST_ACCESS_DENIED_MSG, RETRIEVE_LOADING_ORDER_LIST_ERR_MSG, RETRIEVE_LOADING_ORDER_LIST_SUCCESS_MSG } from '../../../constants/success_error_messages_consts';
import { RETRIEVE_LOADING_ORDERS_LIST_API } from '../../../api/api_data';
import { messageToast } from '../../../utils/Utils';
import { BiErrorCircle } from 'react-icons/bi';
import { Badge, DropdownItem, DropdownMenu, DropdownToggle, Spinner, UncontrolledDropdown } from 'reactstrap';
import { BlockBetween, BlockDes, BlockHead, BlockHeadContent, BlockTitle, Button, DataTableBody, DataTableHead, DataTableItem, DataTableRow, Icon, PaginationComponent } from '../../../components/Component';
import { Link, useNavigate } from 'react-router-dom';
import AuthError from '../../components/page_messaging/AuthError';
import AuthLoader from '../../components/page_messaging/AuthLoader';
import AuthNoData from '../../components/page_messaging/AuthNoData';
import moment from 'moment';
import ModalAdd from '../components/modal/modal_add/ModalAdd';
import { useMinLoading } from "../../../Hooks/useMinLoading";
import usePersistentState from "../../../Hooks/usePersistentState";
import { makeEmployeeDashboardApiRequest } from '../../../api/api_helper';
import { RETRIEVE_LOADING_ORDER_DETAILS_API } from '../../../api/api_data';


const type = app_consts.LOADING_ORDERS_LIST;

const initial_actions_data = {
    view_loading_orders: false,
    create_loading_order: false,
};

const PRODUCT_PREVIEW_LIMIT = 3;

const getLoadingOrderStatusMeta = (status = "") => {
    const normalizedStatus = String(status || "").trim().toLowerCase();

    if (normalizedStatus === String(app_consts.DN_PROCESSED || "").toLowerCase())
    {
        return { color: "success", label: "Processed" };
    }

    if (normalizedStatus === String(app_consts.PENDING_DN_PROCESSING || "").toLowerCase())
    {
        return { color: "warning", label: "Pending Processing" };
    }

    if (normalizedStatus === String(app_consts.UNDERGOING_PROCESSING || "").toLowerCase())
    {
        return { color: "info", label: "Undergoing Processing" };
    }

    if (normalizedStatus.includes("cancel"))
    {
        return { color: "danger", label: "Cancelled" };
    }

    return { color: "secondary", label: status || "Unknown" };
};

const LOADING_ORDER_STATUS_LEGEND = [
    getLoadingOrderStatusMeta(app_consts.DN_PROCESSED),
    getLoadingOrderStatusMeta(app_consts.PENDING_DN_PROCESSING),
    getLoadingOrderStatusMeta(app_consts.UNDERGOING_PROCESSING),
    getLoadingOrderStatusMeta("cancelled"),
];

const resolveLoadingOrderField = (item = {}, key, fallback = "") =>
    item?.loading_order?.[key] ?? item?.[key] ?? fallback;

const resolveLoadingOrderTruckRegistration = (item = {}) =>
    resolveLoadingOrderField(item, "truck_license_plate") ||
    resolveLoadingOrderField(item, "truck_licence_plate") ||
    "--";

const firstNonEmptyProductList = (...candidates) => {
    for (const candidate of candidates)
    {
        if (Array.isArray(candidate) && candidate.length > 0)
        {
            return candidate;
        }
    }

    return [];
};

const getLoadingOrderProducts = (item = {}) =>
    firstNonEmptyProductList(
        item?.products,
        item?.loading_order?.products,
        item?.loading_order_product_info?.products_list
    );

const getLoadingOrderProductNames = (item = {}) =>
    [...new Set(
        getLoadingOrderProducts(item)
            .map((product) => product?.product_name || product?.product_label || product?.product?.product_name || product?.product || product?.name || "")
            .filter(Boolean)
    )];

const formatLoadingOrderTonnage = (item = {}) => {
    const productTonnage = getLoadingOrderProducts(item).reduce(
        (sum, product) => sum + (Number.parseFloat(product?.total_mt_capacity) || 0),
        0
    );

    const totalTonnage = item?.loading_order?.total_tonnage ?? item?.total_tonnage ?? (productTonnage > 0 ? productTonnage : "");
    const parsedTonnage = Number.parseFloat(String(totalTonnage).replace(/[^0-9.-]/g, ""));

    return Number.isFinite(parsedTonnage) ? `${parsedTonnage.toFixed(2)} MT` : "--";
};

const mergeLoadingOrderRowWithDetails = (item = {}, details = null) => {
    if (!details)
    {
        return item;
    }

    return {
        ...item,
        ...details,
        loading_order: {
            ...(item?.loading_order || {}),
            loading_order_id: details?.loading_order_id ?? item?.loading_order?.loading_order_id,
            loading_order_ref_no: details?.loading_order_ref_no ?? item?.loading_order?.loading_order_ref_no,
            loading_order_status_id: details?.loading_order_status_id ?? item?.loading_order?.loading_order_status_id,
            loading_order_type: details?.loading_order_type ?? item?.loading_order?.loading_order_type,
            total_products: details?.total_products ?? item?.loading_order?.total_products,
            total_tonnage: details?.total_tonnage ?? item?.loading_order?.total_tonnage,
            user_account_id: details?.user_account_id ?? item?.loading_order?.user_account_id,
            contact_person: details?.contact_person ?? item?.loading_order?.contact_person,
            contact_mobile: details?.contact_mobile ?? item?.loading_order?.contact_mobile,
            inserted_at: details?.inserted_at ?? item?.loading_order?.inserted_at,
            truck_license_plate: details?.truck_license_plate ?? item?.loading_order?.truck_license_plate,
            truck_licence_plate: details?.truck_licence_plate ?? item?.loading_order?.truck_licence_plate,
            products: details?.products ?? item?.loading_order?.products,
        },
    };
};

const LoadingOrderslist = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [page_data, setPageData] = useState([]);
    const [componentData, setComponentData] = useState(initial_actions_data);
    const [error, setError] = useState("");
    const [rawLoading, setRawLoading] = useState(true);
    const [account_actions, setAccountActions] = useState([]);
    const [sm, updateSm] = useState(false);
    const [searchTerm, setSearchTerm] = usePersistentState('loadingOrders_searchTerm', "");
    const [selectedFilter, setSelectedFilter] = usePersistentState('loadingOrders_selectedFilter', "All");
    const [currentPage, setCurrentPage] = usePersistentState('loadingOrders_currentPage', 1);
    const [itemPerPage] = useState(10);
    const [loadingOrderDetailsByRef, setLoadingOrderDetailsByRef] = usePersistentState('loadingOrders_detailsByRef', {});
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const [add_modal, setAddModal] = useState(false);
    const skipInitialPageResetRef = useRef(true);
    const loadingOrderDetailsInFlightRef = useRef({});

    const { loading, hasFetched } = useMinLoading(rawLoading, 300);

    const shouldIgnoreRowClick = (target) => {
        if (!target || typeof target.closest !== "function") return false;

        return Boolean(
            target.closest("a, button, input, select, textarea, label, .nk-tb-col-tools, .dropdown-toggle, .dropdown-menu")
        );
    };

    const handleRowNavigation = (event, loadingOrderRefNo) => {
        if (!loadingOrderRefNo || shouldIgnoreRowClick(event.target)) return;
        navigate(`/dashboard/loading_orders/${loadingOrderRefNo}`);
    };

    // UseEffect that Runs When the Page Opens
    useEffect(() => {
        refreshList();
    }, []);

    // Retrieve Delivery Notes Tickets
    const retrieve_loading_order_list_func = () => {
        dispatch(
            employee_dashboard_actions_helper(
                loading_retrieve_loading_order_list,
                success_retrieve_loading_order_list,
                fail_retrieve_loading_order_list,
                RETRIEVE_LOADING_ORDER_LIST_ERR_MSG,
                RETRIEVE_LOADING_ORDER_LIST_SUCCESS_MSG,
                app_consts.GET,
                RETRIEVE_LOADING_ORDERS_LIST_API,
                null,
                RETRIEVE_LOADING_ORDER_LIST_ACCESS_DENIED_MSG
            )
        );
        if (localStorage.getItem("log_cred"))
        {
            setAccountActions(JSON.parse(localStorage.getItem('log_cred'))?.user_actions);
        }
    };

    // Listen to Changes In Account Actions
    useEffect(() => {
        if (account_actions)
        {
            let actions_data = initial_actions_data;
            account_actions?.map((module) => {
                if (module.module_name === app_consts.WAREHOUSE_MANAGEMENT) 
                {
                    // Actions Data
                    if (findModuleAction(app_consts.CREATE_LOADING_ORDER, module))
                    {
                        actions_data.create_loading_order = true;
                    }
                }
            });
            setComponentData(actions_data);
        }
    }, [account_actions]);

    // Refresh List Function
    const refreshList = () => {
        setError("");
        setRawLoading(true);
        // Retrieve Weigh Bridge Tickets list
        //   retrieve_user_list_func();
        retrieve_loading_order_list_func();
        setPageData([]);
    };

    // Listen to Loading Order Changes
    const retrieve_loading_order_list = useSelector((state) => state.retrieve_loading_order_list.retrieve_loading_order_list);
    // 
    useEffect(() => {
        if (retrieve_loading_order_list.status !== app_consts.IDLE)
        {
            if (retrieve_loading_order_list.status === app_consts.LOADING)
            {
                setError("");
                setRawLoading(true);
            } else
            {
                if (retrieve_loading_order_list.status === app_consts.FAIL)
                {
                    setRawLoading(false);
                    setError(retrieve_loading_order_list.message);
                    messageToast(RETRIEVE_LOADING_ORDER_LIST_ERR_MSG, app_consts.FAIL);
                } else if (retrieve_loading_order_list.status === app_consts.SUCCESS)
                {
                    setPageData(retrieve_loading_order_list.data);
                    setRawLoading(false);
                }
                dispatch(reset_retrieve_loading_order_list());
            }
        }
    }, [retrieve_loading_order_list]);


    const open_add_modal = () => {
        setAddModal(true);
    };

    const close_add_modal = () => {
        setAddModal(false);
    };

    const successAddFunction = () => {
        close_add_modal();
    };

    const current_page_data = useMemo(() => {
        let filteredData = page_data;

        // Step 1: Apply filter first
        // if (selectedFilter === "Processed")
        // {
        //     filteredData = filteredData?.filter(
        //         (order) =>
        //             order?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status?.toLowerCase() ===
        //             app_consts.DN_PROCESSED?.toLowerCase()
        //     );
        // } else if (selectedFilter === "Pending Processing")
        // {
        //     filteredData = filteredData?.filter(
        //         (order) =>
        //             order?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status?.toLowerCase() ===
        //             app_consts.DN_PENDING?.toLowerCase()
        //     );
        // } else if (selectedFilter === "Cancelled")
        // {
        //     filteredData = filteredData?.filter(
        //         (order) =>
        //             order?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status?.toLowerCase() ===
        //             app_consts.DN_CANCELLED?.toLowerCase()
        //     );
        // }

        // Step 2: Apply search
    if (searchTerm)
    {
            const lowerSearch = searchTerm.toLowerCase();

            filteredData = filteredData?.filter((item) => {
                return (
                    resolveLoadingOrderField(item, "loading_order_ref_no")
                        ?.toLowerCase()
                        .includes(lowerSearch) ||
                    resolveLoadingOrderField(item, "loading_order_type")
                        ?.toLowerCase()
                        .includes(lowerSearch) ||
                    item?.consigner?.name?.toLowerCase().includes(lowerSearch) ||
                    item?.user_account?.unique_identification_number
                        ?.toLowerCase()
                        .includes(lowerSearch) ||
                    resolveLoadingOrderField(item, "contact_person")
                        ?.toLowerCase()
                        .includes(lowerSearch) ||
                    resolveLoadingOrderField(item, "contact_mobile")
                        ?.toLowerCase()
                        .includes(lowerSearch) ||
                    String(resolveLoadingOrderField(item, "total_products", ""))
                        ?.toString()
                        .toLowerCase()
                        .includes(lowerSearch) ||
                    String(resolveLoadingOrderField(item, "total_tonnage", ""))
                        ?.toString()
                        .toLowerCase()
                        .includes(lowerSearch) ||
                    resolveLoadingOrderTruckRegistration(item)
                        ?.toLowerCase()
                        .includes(lowerSearch) ||
                    getLoadingOrderProductNames(item)
                        .join(" ")
                        .toLowerCase()
                        .includes(lowerSearch) ||
                    (resolveLoadingOrderField(item, "inserted_at") &&
                        moment(resolveLoadingOrderField(item, "inserted_at"))
                            .format("YYYY-MM-DD h:mm:ss")
                            .toLowerCase()
                            .includes(lowerSearch)) ||
                    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status
                        ?.toLowerCase()
                        .includes(lowerSearch)
                );
            });
        }

        return filteredData || [];
    }, [searchTerm, selectedFilter, page_data]);

    useEffect(() => {
        if (skipInitialPageResetRef.current) {
            skipInitialPageResetRef.current = false;
            return;
        }
        setCurrentPage(1);
    }, [searchTerm, selectedFilter, setCurrentPage]);

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(current_page_data.length / itemPerPage));
        if (currentPage > maxPage) {
            setCurrentPage(maxPage);
        }
    }, [currentPage, current_page_data.length, itemPerPage, setCurrentPage]);
    
    // Get current list, pagination
    const indexOfLastItem = currentPage * itemPerPage;
    const indexOfFirstItem = indexOfLastItem - itemPerPage;
    const currentItems = current_page_data?.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        const refsToFetch = currentItems
            ?.map((item) => resolveLoadingOrderField(item, "loading_order_ref_no"))
            .filter((refNo) => refNo && !loadingOrderDetailsByRef[refNo] && !loadingOrderDetailsInFlightRef.current[refNo]);

        if (!Array.isArray(refsToFetch) || refsToFetch.length === 0)
        {
            return;
        }

        let shouldUpdateState = true;

        const fetchLoadingOrderDetails = async () => {
            const results = await Promise.allSettled(
                refsToFetch.map(async (loadingOrderRefNo) => {
                    loadingOrderDetailsInFlightRef.current[loadingOrderRefNo] = true;

                    try
                    {
                        const response = await makeEmployeeDashboardApiRequest(
                            app_consts.GET,
                            `${RETRIEVE_LOADING_ORDER_DETAILS_API}/${loadingOrderRefNo}`
                        );

                        return {
                            loadingOrderRefNo,
                            details: response?.data?.data || null,
                        };
                    } finally
                    {
                        delete loadingOrderDetailsInFlightRef.current[loadingOrderRefNo];
                    }
                })
            );

            if (!shouldUpdateState)
            {
                return;
            }

            setLoadingOrderDetailsByRef((previousState) => {
                const nextState = { ...previousState };

                results.forEach((result) => {
                    if (result.status !== "fulfilled")
                    {
                        return;
                    }

                    if (result.value?.details)
                    {
                        nextState[result.value.loadingOrderRefNo] = result.value.details;
                    }
                });

                return nextState;
            });
        };

        fetchLoadingOrderDetails();

        return () => {
            shouldUpdateState = false;
        };
    }, [currentItems, loadingOrderDetailsByRef]);
    
    const showNoData = hasFetched && !loading && !error && (!page_data || page_data.length === 0);
    const shouldShowInitialLoader = !hasFetched && !error && (!page_data || page_data.length === 0);



    return (
        <>
            <Head title="Loading Orders" />
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle page>Loading Orders</BlockTitle>
                            <BlockDes className="text-soft">
                                <p>{!(loading || error) && ` Loading Orders ${current_page_data?.length}`}</p>
                            </BlockDes>
                            {!(loading) && (
                                <div className="mt-2">
                                    <Link
                                        onClick={(e) => {
                                            e.preventDefault();
                                            refreshList();
                                        }}
                                    >
                                        <Button color="light" outline className="bg-white d-none d-sm-inline-flex">
                                            <span>Refresh List</span>
                                        </Button>
                                    </Link>
                                </div>
                            )}
                        </BlockHeadContent>
                        {
                            !(loading || error) && (
                                <>
                                    <BlockHeadContent>
                                        <div className="toggle-wrap nk-block-tools-toggle">
                                            <a
                                                href="#more"
                                                className="btn btn-icon btn-trigger toggle-expand me-n1"
                                                onClick={(ev) => {
                                                    ev.preventDefault();
                                                    updateSm(!sm);
                                                }}
                                            >
                                                <Icon name="more-v"></Icon>
                                            </a>
                                            <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                                                <ul className="nk-block-tools g-3">
                                                    {
                                                        findModuleAction(app_consts.CREATE_LOADING_ORDER, componentData) !== null && (
                                                            <>
                                                                {!loading && !error && (
                                                                    <li>
                                                                        <Button color="light" outline className="btn-white">
                                                                            <Icon name="download-cloud"></Icon>
                                                                            <span>Export to CSV</span>
                                                                        </Button>
                                                                    </li>
                                                                )}
                                                                <li className="nk-block-tools-opt">
                                                                    <Button
                                                                        className="toggle btn-icon d-md-none"
                                                                        color="primary"
                                                                        onClick={() => {
                                                                            open_add_modal();
                                                                        }}
                                                                    >
                                                                        <Icon name="plus"></Icon>
                                                                    </Button>
                                                                    <Button
                                                                        className="toggle d-none d-md-inline-flex"
                                                                        color="primary"
                                                                        onClick={() => {
                                                                            open_add_modal();
                                                                        }}
                                                                    >
                                                                        <Icon name="plus"></Icon>
                                                                        <span>Add Loading Order</span>
                                                                    </Button>
                                                                </li>
                                                            </>
                                                        )
                                                    }
                                                </ul>
                                            </div>
                                        </div>
                                    </BlockHeadContent>
                                </>
                            )
                        }
                    </BlockBetween>
                </BlockHead>

                {
                    (loading || shouldShowInitialLoader) ?
                        <>
                            <AuthLoader loading_message="Retrieving Loading Orders..." />
                        </>
                        :
                        <>
                            {
                                error ?
                                    <>
                                        <AuthError error_message={error} />
                                    </>
                                    :
                                    <>
                                        {
                                            page_data && page_data?.length > 0 ?
                                                <>
                                                    <ul className="nk-block-tools g-3 d-flex justify-end">
                                                        <li>
                                                            <div className="">
                                                                <div className="form-icon form-icon-right">
                                                                    <Icon name="search"></Icon>
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    id="default-04"
                                                                    value={searchTerm}
                                                                    onChange={e => setSearchTerm(e.target.value)}
                                                                    placeholder="Search for Loading Orders"
                                                                />
                                                            </div>
                                                        </li>
                                                        {/* <li className="preview-item">
                                                            <UncontrolledDropdown>
                                                                <div className="btn-group">
                                                                    <DropdownToggle color="light" outline className="dropdown-toggle-split btn-white">
                                                                        <Icon name="chevron-down"></Icon>

                                                                        <span>{selectedFilter}</span>
                                                                    </DropdownToggle>
                                                                </div>
                                                                <DropdownMenu className="mt-3">
                                                                    <ul className="link-list-opt">
                                                                        <li>
                                                                            <DropdownItem
                                                                                tag="a"
                                                                                href="#"
                                                                                onClick={(ev) => {
                                                                                    ev.preventDefault();
                                                                                    filterLoadingOrders("All");
                                                                                }}
                                                                            >
                                                                                <span>All</span>
                                                                            </DropdownItem>
                                                                        </li>
                                                                        <li>
                                                                            <DropdownItem
                                                                                tag="a"
                                                                                href="#"
                                                                                onClick={(ev) => {
                                                                                    ev.preventDefault();
                                                                                    filterLoadingOrders("Processed");
                                                                                }}
                                                                            >
                                                                                <span>Processed</span>
                                                                            </DropdownItem>
                                                                        </li>
                                                                        <li>
                                                                            <DropdownItem
                                                                                tag="a"
                                                                                href="#"
                                                                                onClick={(ev) => {
                                                                                    ev.preventDefault();
                                                                                    filterLoadingOrders("Pending Processing");
                                                                                }}
                                                                            >
                                                                                <span>Pending Processing</span>
                                                                            </DropdownItem>
                                                                        </li>
                                                                        <li>
                                                                            <DropdownItem
                                                                                tag="a"
                                                                                href="#"
                                                                                onClick={(ev) => {
                                                                                    ev.preventDefault();
                                                                                    filterLoadingOrders("Cancelled");
                                                                                }}
                                                                            >
                                                                                <span>Cancelled</span>
                                                                            </DropdownItem>
                                                                        </li>
                                                                    </ul> 
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </li>*/}
                                                    </ul>
                                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3 px-1">
                                                        <span className="text-soft small">Status legend:</span>
                                                        {LOADING_ORDER_STATUS_LEGEND.map((statusItem) => (
                                                            <div key={statusItem.label} className="d-inline-flex align-items-center gap-1 me-2">
                                                                <Badge color={statusItem.color} pill style={{ width: "12px", height: "12px", padding: 0, display: "inline-block" }} />
                                                                <span className="small text-soft">{statusItem.label}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="nk-tb-list is-separate is-medium mt-1 mb-3">
                                                        <div className="card-inner p-0">
                                                            <DataTableBody>
                                                                <DataTableHead>
                                                                    <DataTableRow size="sm">
                                                                        <span>Ref No.</span>
                                                                    </DataTableRow>
                                                                    <DataTableRow>
                                                                        <span>Client</span>
                                                                    </DataTableRow>
                                                                    <DataTableRow>
                                                                        <span>Contact Person</span>
                                                                    </DataTableRow>
                                                                    <DataTableRow>
                                                                        <span>No. of Products</span>
                                                                    </DataTableRow>
                                                                    <DataTableRow>
                                                                        <span>Products</span>
                                                                    </DataTableRow>
                                                                    <DataTableRow>
                                                                        <span>Truck Reg No</span>
                                                                    </DataTableRow>
                                                                    <DataTableRow>
                                                                        <span>Tonnage</span>
                                                                    </DataTableRow>
                                                                    <DataTableRow>
                                                                        <span>Created On</span>
                                                                    </DataTableRow>
                                                                    <DataTableRow size="md">
                                                                        <span>Status</span>
                                                                    </DataTableRow>
                                                                    <DataTableRow size="md">
                                                                        <span>Action</span>
                                                                    </DataTableRow>
                                                                </DataTableHead>
                                                                {/*  */}
                                                                {currentItems?.length > 0
                                                                    ? currentItems.map((item) => {
                                                                        const loadingOrderRefNo = resolveLoadingOrderField(item, "loading_order_ref_no");
                                                                        const hydratedItem = mergeLoadingOrderRowWithDetails(item, loadingOrderDetailsByRef[loadingOrderRefNo]);
                                                                        const productNames = getLoadingOrderProductNames(hydratedItem);
                                                                        const loadingOrderStatus = item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status || "";
                                                                        const statusMeta = getLoadingOrderStatusMeta(loadingOrderStatus);

                                                                        return (
                                                                                <DataTableItem
                                                                                key={loadingOrderRefNo}
                                                                                className="cursor-pointer"
                                                                                role="link"
                                                                                tabIndex={0}
                                                                                onClick={(event) => handleRowNavigation(event, loadingOrderRefNo)}
                                                                                onKeyDown={(event) => {
                                                                                    if (event.key !== "Enter" && event.key !== " ") return;
                                                                                    event.preventDefault();
                                                                                    handleRowNavigation(event, loadingOrderRefNo);
                                                                                }}
                                                                            >
                                                                                <DataTableRow size="sm">
                                                                                    <Link to={`/dashboard/loading_orders/${loadingOrderRefNo}`}>
                                                                                        <span className="tb-product">
                                                                                            <span className="title">{resolveLoadingOrderField(hydratedItem, "loading_order_ref_no", "--")}</span>
                                                                                        </span>
                                                                                        <div>
                                                                                            <span className="fw-medium">Type: </span>
                                                                                            <span className="tb-sub">{resolveLoadingOrderField(hydratedItem, "loading_order_type") ? formatToSentenceCase(resolveLoadingOrderField(hydratedItem, "loading_order_type")) : "--"}</span>
                                                                                        </div>
                                                                                    </Link>
                                                                                </DataTableRow>
                                                                                <DataTableRow>
                                                                                    <span className="tb-sub">{
                                                                                        item?.consigner?.name
                                                                                            ? item?.consigner?.name.length > 20
                                                                                                ? `${item?.consigner?.name.substring(0, 20)}...`
                                                                                                : item?.consigner?.name
                                                                                            : "---"
                                                                                    }</span>
                                                                                    <div>
                                                                                        <span className="fw-medium">ID: </span>
                                                                                        <span className="tb-sub">{item?.user_account?.unique_identification_number || "--"}</span>
                                                                                    </div>
                                                                                </DataTableRow>
                                                                                <DataTableRow>
                                                                                    <div>
                                                                                        <span className="fw-medium">Name: </span>
                                                                                        <span className="tb-sub">{resolveLoadingOrderField(hydratedItem, "contact_person", "--")}</span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <span className="fw-medium">Mobile: </span>
                                                                                        <span className="tb-sub">{resolveLoadingOrderField(hydratedItem, "contact_mobile", "--")}</span>
                                                                                    </div>
                                                                                </DataTableRow>
                                                                                <DataTableRow>
                                                                                    <div>
                                                                                        <span className="fw-medium">Count: </span>
                                                                                        <span className="tb-sub">{resolveLoadingOrderField(hydratedItem, "total_products", "--")}</span>
                                                                                    </div>
                                                                                </DataTableRow>
                                                                                <DataTableRow>
                                                                                    <div>
                                                                                        {productNames?.length > 0 ? (
                                                                                            <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                                                                                {productNames.slice(0, PRODUCT_PREVIEW_LIMIT)?.map((productName) => (
                                                                                                    <li key={productName} className="tb-sub">{productName}</li>
                                                                                                ))}
                                                                                                {productNames.length > PRODUCT_PREVIEW_LIMIT && (
                                                                                                    <li className="tb-sub">+{productNames.length - PRODUCT_PREVIEW_LIMIT} more</li>
                                                                                                )}
                                                                                            </ul>
                                                                                        ) : (
                                                                                            <span className="tb-sub">--</span>
                                                                                        )}
                                                                                    </div>
                                                                                </DataTableRow>
                                                                                <DataTableRow>
                                                                                    <span className="tb-sub">{resolveLoadingOrderTruckRegistration(hydratedItem)}</span>
                                                                                </DataTableRow>
                                                                                <DataTableRow>
                                                                                    <span className="tb-sub">{formatLoadingOrderTonnage(hydratedItem)}</span>
                                                                                </DataTableRow>
                                                                                <DataTableRow>
                                                                                    <span className="tb-sub">{resolveLoadingOrderField(hydratedItem, "inserted_at")
                                                                                        ? moment(resolveLoadingOrderField(hydratedItem, "inserted_at")).format("YYYY-MM-DD h:mm:ss") : "--"}</span>
                                                                                </DataTableRow>
                                                                                <DataTableRow size="md">
                                                                                    <span className="tb-odr-status">
                                                                                        <Badge
                                                                                            color={statusMeta.color}
                                                                                            pill
                                                                                            title={statusMeta.label}
                                                                                            style={{ width: "18px", height: "18px", padding: 0, display: "inline-block" }}
                                                                                        />
                                                                                    </span>
                                                                                </DataTableRow>
                                                                                <DataTableRow className="nk-tb-col-tools">
                                                                                    <div className="tb-odr-btns d-none d-sm-inline">
                                                                                        <Link to={`/dashboard/loading_orders/${loadingOrderRefNo}`}>
                                                                                            <Button color="primary" size="sm" className="btn btn-dim">
                                                                                                View
                                                                                            </Button>
                                                                                        </Link>
                                                                                    </div>
                                                                                    <Button className="btn-pd-auto d-sm-none">
                                                                                        <Icon name="chevron-right"></Icon>
                                                                                    </Button>
                                                                                </DataTableRow>
                                                                            </DataTableItem>
                                                                        );
                                                                    })
                                                                    : null}
                                                            </DataTableBody>
                                                            {currentItems?.length > 0 && (
                                                                <div className="mt-3">
                                                                    <PaginationComponent
                                                                        itemPerPage={itemPerPage}
                                                                        totalItems={current_page_data?.length}
                                                                        paginate={paginate}
                                                                        currentPage={currentPage}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </>
                                                :
                                                <>
                                                    <AuthNoData no_data_message={"No Loading Orders Data Found."} />
                                                </>
                                        }
                                        <ModalAdd
                                            openModal={add_modal}
                                            setOpenModal={setAddModal}
                                            modalTitle="Add Loading Order"
                                            modalDescription="Please Complete this form to Add a Loading Order"
                                            type={type}
                                            succFunc={successAddFunction}
                                            size="xl"
                                        />
                                    </>
                            }
                        </>
                }
            </Content>
        </>
    );
};

export default LoadingOrderslist;
